import { formatCsvExportCell } from "../../../lib/persona-csv-cells.js";
import type { HubChromeOpportunityRow } from "./hub-chrome.types.js";
import {
  drivingIdentifier,
  type HubOpportunityColumn,
} from "./hub-opportunity-columns.js";

/** The pinned identifier column is sortable but never hidden or reordered. */
export const DRIVING_COLUMN_ID = "driving";

export type HubSortDirection = "asc" | "desc";

export interface HubOpportunitySort {
  columnId: string;
  direction: HubSortDirection;
}

export interface HubOpportunityTableState {
  hiddenColumnIds: string[];
  columnOrder: string[];
  sort: HubOpportunitySort | null;
  expandedGroupKeys: string[];
}

/** Leftover Opportunities opens on the largest findings gap. */
export const COMPETITOR_HUB_DEFAULT_SORT: HubOpportunitySort = {
  columnId: "findings",
  direction: "desc",
};

/** Show the findings default once competitor columns are on screen. */
export function displayedHubSort(
  sort: HubOpportunitySort | null,
  hasCompetitorColumns: boolean,
): HubOpportunitySort | null {
  if (sort) return sort;
  return hasCompetitorColumns ? COMPETITOR_HUB_DEFAULT_SORT : null;
}

export const DEFAULT_HUB_OPPORTUNITY_TABLE_STATE: HubOpportunityTableState = {
  hiddenColumnIds: [],
  columnOrder: [],
  sort: null,
  expandedGroupKeys: [],
};

/** Mirrors `isHubServerSortableColumn` in `persona-opportunity-hub-window.ts`. */
const HUB_SERVER_SORTABLE_COLUMN_IDS = new Set([
  "runDate",
  "type",
  "driving",
  "findings",
  "spend",
  "benchmarkSpend",
  "competitorSpend",
  "creatives",
  "competitors",
  "stickRate",
  "duplicated",
  "investmentIndex",
  "state",
  "status",
  "angle",
  "painPoint",
  "motivation",
  "action",
  "flow",
  "feature",
  "subFeature",
  "engine",
  "topicTitle",
  "topicDescription",
  "suggestion",
  "kpi",
  "competitorKpi",
  "benchmarkKpi",
  "industry",
]);

export function isHubOpportunityServerSortableColumn(columnId: string): boolean {
  return HUB_SERVER_SORTABLE_COLUMN_IDS.has(columnId);
}

/** Ascending → descending → unsorted, matching the legacy opportunities table. */
export function nextSort(
  current: HubOpportunitySort | null,
  columnId: string,
): HubOpportunitySort | null {
  if (current?.columnId !== columnId) return { columnId, direction: "asc" };
  if (current.direction === "asc") return { columnId, direction: "desc" };
  return null;
}

export function orderColumns(
  columns: HubOpportunityColumn[],
  columnOrder: string[],
): HubOpportunityColumn[] {
  if (columnOrder.length === 0) return columns;
  const byId = new Map(columns.map((column) => [column.id, column]));
  const ordered = columnOrder
    .map((id) => byId.get(id))
    .filter((column): column is HubOpportunityColumn => column != null);
  const missing = columns.filter((column) => !columnOrder.includes(column.id));
  return [...ordered, ...missing];
}

export function visibleColumns(
  columns: HubOpportunityColumn[],
  hiddenColumnIds: string[],
): HubOpportunityColumn[] {
  return columns.filter((column) => !hiddenColumnIds.includes(column.id));
}

function sortValueOf(
  row: HubChromeOpportunityRow,
  columnId: string,
  columns: HubOpportunityColumn[],
): string | number | null | undefined {
  if (columnId === DRIVING_COLUMN_ID) return drivingIdentifier(row);
  return columns.find((column) => column.id === columnId)?.sortValue?.(row);
}

export function sortOpportunityRows(
  rows: HubChromeOpportunityRow[],
  sort: HubOpportunitySort | null,
  columns: HubOpportunityColumn[],
): HubChromeOpportunityRow[] {
  if (!sort) return rows;
  const factor = sort.direction === "asc" ? 1 : -1;

  return [...rows].sort((left, right) => {
    const a = sortValueOf(left, sort.columnId, columns);
    const b = sortValueOf(right, sort.columnId, columns);
    // Blanks sink to the bottom in both directions so the sorted edge stays meaningful.
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === "number" && typeof b === "number") return (a - b) * factor;
    return String(a).localeCompare(String(b)) * factor;
  });
}

function csvCell(column: HubOpportunityColumn, row: HubChromeOpportunityRow): string {
  if (column.csvValue) {
    return formatCsvExportCell(column.csvValue(row));
  }
  const sortable = column.sortValue?.(row);
  if (sortable != null) {
    return formatCsvExportCell(sortable);
  }
  const rendered = column.cell(row);
  if (typeof rendered === "string" || typeof rendered === "number") {
    return formatCsvExportCell(rendered);
  }
  return formatCsvExportCell("");
}

export function buildOpportunityCsv(
  rows: HubChromeOpportunityRow[],
  columns: HubOpportunityColumn[],
  identifierHeader: string,
): string {
  const header = [identifierHeader, ...columns.map((column) => column.header)];
  const lines = rows.map((row) => [
    formatCsvExportCell(drivingIdentifier(row)),
    ...columns.map((column) => csvCell(column, row)),
  ]);
  return [header, ...lines].map((line) => line.join(",")).join("\n");
}